import OCTContextBase from "./octContextBase";
import { octColorSettings, generatePalette, octThicknessGradient, heidelbergThicknessGradient, zeissThicknessGradient, heidelbergDifferenceGradient, calculatePixelDifference } from "../canvas/canvasUtils";
import { getDefaultMeasurementIndex, getMeasurementData, calculateThicknessMapAdjustment, getThicknessMapSize } from "./octUtils";
import { defaultSize } from "../canvas/geometry";
import store from "../store";

export default class SpectralisComparisonViewContext extends OCTContextBase {
  constructor(group, exam, view) {
    super(group, exam, "spectralisComparisonView");

    this.view = view;

    this.loadImages = false;
    this.loadComposite = false;
    this.loadHD = false;

    this.octColorSettings = octColorSettings();
    this.slicePalette = octThicknessGradient;

    this.showThicknessMap = true;
    this.thicknessMapColorSettings = null;
    this.thicknessMapPalette = this.groupName.toLowerCase().includes("volume") || this.groupName.toLowerCase().includes("cube") ? heidelbergThicknessGradient : zeissThicknessGradient;
    this.thicknessMapMinThicknessUm = 0;
    this.thicknessMapMaxThicknessUm = 600;
    this.thicknessMapThicknessStepUm = 100;
    this.thicknessMapOpacity = 1.0;

    this.relativeMapSize = defaultSize();

    this.hasDifferenceMap = false;
    this.otherExSrNo = null;
    this.alignFoveas = null;

    this.differenceCanvas = null;
    this.differenceMapColorSettings = null;
    this.differenceMapMinThicknessUm = -100;
    this.differenceMapMaxThicknessUm = 100;
    this.differenceMapThicknessStepUm = 50;
    this.differenceMapPalette = this.groupName.toLowerCase().includes("circle") || this.groupName.toLowerCase().includes("star") ? null : heidelbergDifferenceGradient;
    this.differenceMapOpacity = 1.0;

    this.showSegmentation = false;
    this.isMeasuring = false;
    this.measureData = null;
  }

  startupTask() {
    return store.dispatch("getOCTInfo", { exSrNo: this.exSrNo, groupName: this.groupName })
      .then(data => this.setOCTInfo(data.data));
  }

  setOCTInfo(data) {
    super.setOCTInfo(data);

    if (this.thicknessMap) {
      let colorPalette = generatePalette(this.thicknessMapPalette);
      let linearAdjustment = calculateThicknessMapAdjustment(this.thicknessMapMinThicknessUm,
        this.thicknessMapMaxThicknessUm, this.thicknessMap.depthSpacingMm);
      this.thicknessMapColorSettings = { colorPalette, linearAdjustment };

      // calculate the thickness map here in case the image was loaded previously
      if (this.thicknessMap.imgHtmlObject && this.differenceMapPalette) this.loadThicknessMap(this.thicknessMap);
    }
    this.relativeMapSize = getThicknessMapSize(this);

    if (this.loadImages)
      this.refreshImages();
  }

  refreshImages() {
    return store.dispatch("loadOCTImagesForMultiview");
  }

  toggleSegmentation() {
    this.showSegmentation = !this.showSegmentation;
  }
  toggleMeasuring() {
    if (!this.isMeasuring) {
      this.setDefaultMeasurement();
      if (this.measureData) this.isMeasuring = true;
    } else {
      this.isMeasuring = false;
    }
  }
  setDefaultMeasurement() {
    let index = getDefaultMeasurementIndex(this.sliceInfo);
    this.setMeasurementIndex(index);
  }

  setMeasurementIndex(index) {
    this.measureData = getMeasurementData(index, this.sliceCurrent, this.sliceInfo);
  }

  toggleColorPalette() {
    this.octColorSettings.isInverted = false;
    if (this.octColorSettings.colorPalette) this.octColorSettings.colorPalette = null;
    else this.octColorSettings.colorPalette = this.getSlicePalette();
  }
  toggleInverted() {
    this.octColorSettings.colorPalette = null;
    this.octColorSettings.isInverted = !this.octColorSettings.isInverted;
  }
  getSlicePalette() {
    return generatePalette(this.slicePalette);
  }

  updateThicknessMapOpacity(opacity) {
    this.differenceMapOpacity = opacity;
  }

  loadThicknessMap() {
    store.commit("calculateDifferenceMap");
  }

  calculateDifferenceMap(other, alignFoveas) {
    // don't recalculate difference map with same exam, just get it once
    if (this.hasDifferenceMap && other.exSrNo === this.otherExSrNo && alignFoveas === this.alignFoveas) return;

    if (!this.thicknessMap || !this.thicknessMap.imgHtmlObject) return;
    if (!other || !other.thicknessMap || !other.thicknessMap.imgHtmlObject) return;
    // ensure that the difference gradient exists to calculate the difference image
    let baselineImg = other.thicknessMap.imgHtmlObject;
    let compareImg = this.thicknessMap.imgHtmlObject;

    // can't calculate difference if the images are different sizes
    if (baselineImg.width !== compareImg.width || baselineImg.height !== compareImg.height) return;

    this.generateDifferenceColorSettings();

    let canvas = document.createElement("canvas");
    let ctx = canvas.getContext("2d");
    let baselineData = this.getImgData(canvas, ctx, baselineImg);
    let baselineBuffer = new Uint32Array(baselineData.data.buffer);
    let compareData = this.getImgData(canvas, ctx, compareImg);
    let compareBuffer = new Uint32Array(compareData.data.buffer);
    let destData = ctx.createImageData(baselineImg.width, baselineImg.height);
    let writeArrayBuffer = new ArrayBuffer(destData.data.length);
    let writeBuffer = new Uint32Array(writeArrayBuffer);

    let { offsetX, offsetY } = this.calculateFoveaOffset(other, alignFoveas, compareImg, baselineImg);
    calculatePixelDifference(compareImg, offsetX, offsetY, compareBuffer, baselineBuffer, writeBuffer, this.differenceMapColorSettings);
    destData.data.set(new Uint8ClampedArray(writeArrayBuffer));
    ctx.putImageData(destData, 0, 0);
    this.differenceCanvas = canvas;
  }

  getImgData(canvas, ctx, img) {
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0, img.width, img.height);
    return ctx.getImageData(0, 0, img.width, img.height);
  }

  generateDifferenceColorSettings() {
    if (this.differenceMapColorSettings || !this.differenceMapPalette) return;
    let differenceColorPalette = generatePalette(this.differenceMapPalette);
    let differenceLinearAdjustment = calculateThicknessMapAdjustment(this.differenceMapMinThicknessUm,
      this.differenceMapMaxThicknessUm, this.thicknessMap.depthSpacingMm );
    this.differenceMapColorSettings = { colorPalette: differenceColorPalette, linearAdjustment: differenceLinearAdjustment };
  }

  calculateFoveaOffset(other, alignFoveas, compareImg, baselineImg) {
    let offsetX = 0, offsetY = 0;
    if (alignFoveas) {
      let compareFoveaX = this.foveaOffset.x * compareImg.width;
      let compareFoveaY = this.foveaOffset.y * compareImg.height;
      let baselineFoveaX = other.foveaOffset.x * baselineImg.width;
      let baselineFoveaY = other.foveaOffset.y * baselineImg.height;
      offsetX = Math.floor(baselineFoveaX - compareFoveaX);
      offsetY = Math.floor(baselineFoveaY - compareFoveaY);
    }
    return { offsetX, offsetY };
  }

}